import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown } from "lucide-react";

import { apiError } from "../../../shared/api/client";
import {
  ADJUSTMENT_KIND_LABELS,
  membershipApi,
  membershipQueryKeys,
  type SpendAdjustmentKind,
} from "../../../shared/api/membership";
import { formatDate, formatVndWithUnit } from "../../../shared/format";
import { useEntitlement } from "../../../shared/entitlement/useEntitlement";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Hạng thành viên của một bệnh nhân, kèm ghi nhận chi tiêu chuyển đổi (#18734).
 *
 * <p>Renders nothing without {@code module.membership} — Pro-only, and a card announcing a feature
 * the clinic does not have would be an advert inside a patient record.
 *
 * <p>Invoice spend and carried-over spend are shown as separate lines rather than one total. A tier
 * earned by real invoices and one granted by a manual entry should not look identical to whoever is
 * reading the record — that distinction is the whole reason the backend keeps them apart.
 *
 * <p>Staff see a tier resting on unconfirmed thresholds; the patient's own app does not (#18871).
 * The card says so outright, because otherwise reception would read a tier here, tell the patient,
 * and be contradicted by the patient's phone.
 */
export function MembershipCard({ patientId }: { patientId: string }) {
  const { enabled } = useEntitlement("module.membership");
  const qc = useQueryClient();
  const [adjusting, setAdjusting] = useState(false);

  const membership = useQuery({
    queryKey: membershipQueryKeys.ofPatient(patientId),
    queryFn: () => membershipApi.ofPatient(patientId),
    enabled,
  });
  const adjustments = useQuery({
    queryKey: membershipQueryKeys.adjustments(patientId),
    queryFn: () => membershipApi.adjustments(patientId),
    enabled,
  });

  if (!enabled) return null;

  const m = membership.data;

  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <Crown className="size-4" />
            Hạng thành viên
          </h3>
          {m?.tierName ? (
            <Badge variant={m.provisionalThresholds ? "outline" : "default"}>{m.tierName}</Badge>
          ) : (
            <Badge variant="secondary">Chưa có hạng</Badge>
          )}
        </div>

        {membership.isLoading && <Skeleton className="h-20" />}
        {membership.isError && (
          <p className="text-sm text-destructive">{apiError(membership.error).detail}</p>
        )}

        {m && (
          <div className="space-y-1.5 text-sm">
            <Row label="Tổng chi tiêu" value={formatVndWithUnit(m.totalSpentVnd)} strong />
            <Row label="— từ hoá đơn" value={formatVndWithUnit(m.invoiceSpentVnd)} />
            {m.adjustedSpentVnd !== 0 && (
              <Row
                label="— chuyển đổi / cấp tay"
                value={formatVndWithUnit(m.adjustedSpentVnd)}
                muted
              />
            )}
            {m.nextTierName && (
              <p className="pt-1 text-xs text-muted-foreground">
                Còn <strong className="text-foreground">
                  {formatVndWithUnit(m.spendToNextTierVnd ?? 0)}
                </strong>{" "}
                nữa để lên hạng {m.nextTierName}.
              </p>
            )}
            {m.provisionalThresholds && (
              <p className="pt-1 text-xs text-amber-700 dark:text-amber-500">
                Ngưỡng hạng vẫn là mức gợi ý mặc định, phòng khám chưa xác nhận — nên{" "}
                <strong>bệnh nhân chưa nhìn thấy hạng này</strong> trên ứng dụng. Đặt lại ngưỡng ở
                Thiết lập → Hạng thành viên.
              </p>
            )}
          </div>
        )}

        {!adjusting ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setAdjusting(true)}
          >
            Ghi nhận chi tiêu cũ
          </Button>
        ) : (
          <AdjustForm
            patientId={patientId}
            onDone={() => {
              setAdjusting(false);
              void qc.invalidateQueries({ queryKey: membershipQueryKeys.ofPatient(patientId) });
              void qc.invalidateQueries({ queryKey: membershipQueryKeys.adjustments(patientId) });
            }}
          />
        )}

        {!!adjustments.data?.length && (
          <ul className="mt-3 space-y-1 border-t pt-3 text-xs text-muted-foreground">
            {adjustments.data.map((a) => (
              <li key={a.id} className="flex justify-between gap-2">
                <span className="min-w-0 truncate">
                  {ADJUSTMENT_KIND_LABELS[a.kind] ?? a.kind} · {a.reason}
                </span>
                <span className="shrink-0 font-mono tabular-nums">
                  {a.amountVnd > 0 ? "+" : ""}
                  {formatVndWithUnit(a.amountVnd)} · {formatDate(a.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`flex justify-between ${muted ? "text-muted-foreground" : ""}`}>
      <span>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}

/** Ghi một dòng vào sổ cái điều chỉnh. Lý do bắt buộc — API từ chối nếu thiếu. */
function AdjustForm({ patientId, onDone }: { patientId: string; onDone: () => void }) {
  const [amount, setAmount] = useState(0);
  const [kind, setKind] = useState<SpendAdjustmentKind>("MIGRATION");
  const [reason, setReason] = useState("");

  const save = useMutation({
    mutationFn: () => membershipApi.adjust(patientId, amount, kind, reason.trim()),
    onSuccess: onDone,
  });

  return (
    <div className="mt-3 space-y-2 border-t pt-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label htmlFor="adjAmount">Số tiền (₫)</Label>
          <Input
            id="adjAmount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Nhập số âm để trừ lại nếu đã ghi nhầm — bản ghi cũ vẫn được giữ.
          </p>
        </div>
        <div>
          <Label htmlFor="adjKind">Loại</Label>
          <select
            id="adjKind"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={kind}
            onChange={(e) => setKind(e.target.value as SpendAdjustmentKind)}
          >
            {(Object.keys(ADJUSTMENT_KIND_LABELS) as SpendAdjustmentKind[]).map((k) => (
              <option key={k} value={k}>
                {ADJUSTMENT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor="adjReason">Lý do</Label>
        <Input
          id="adjReason"
          value={reason}
          placeholder="VD: chuyển dữ liệu từ phần mềm cũ, hoá đơn 2023–2025"
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      {save.isError && <p className="text-sm text-destructive">{apiError(save.error).detail}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone}>
          Huỷ
        </Button>
        <Button
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending || amount === 0 || !reason.trim()}
        >
          {save.isPending ? "Đang lưu…" : "Ghi nhận"}
        </Button>
      </div>
    </div>
  );
}
